import { getTenantKey } from "@/stores/tenant-storage-helpers";
import { useTenantStore } from "@/stores/tenant-store";

// Google requires a 30-day attribution window per
// https://developers.google.com/actions-center/verticals/appointments/redirect/integration-steps/conversion-tracking
// and specifies that the timer resets whenever a new referral replaces the
// stored token — hence writeRwgAttribution() unconditionally stamps storedAt.
const STORAGE_KEY = "rwg-attribution";
const TTL_MS = 30 * 24 * 60 * 60 * 1000;

export type RwgAttribution = {
  token: string | null;
  merchantId: string | null;
};

type Persisted = RwgAttribution & { storedAt: number };

const currentKey = () =>
  getTenantKey(STORAGE_KEY, useTenantStore.getState().getCurrentTenant());

export function readRwgAttribution(): RwgAttribution {
  if (typeof window === "undefined") return { token: null, merchantId: null };
  try {
    const raw = localStorage.getItem(currentKey());
    if (!raw) return { token: null, merchantId: null };
    const parsed: Persisted = JSON.parse(raw);
    if (!parsed?.storedAt || Date.now() - parsed.storedAt > TTL_MS) {
      localStorage.removeItem(currentKey());
      return { token: null, merchantId: null };
    }
    return {
      token: parsed.token ?? null,
      merchantId: parsed.merchantId ?? null,
    };
  } catch {
    return { token: null, merchantId: null };
  }
}

export function writeRwgAttribution(next: RwgAttribution) {
  if (typeof window === "undefined") return;
  try {
    const payload: Persisted = { ...next, storedAt: Date.now() };
    localStorage.setItem(currentKey(), JSON.stringify(payload));
  } catch {
    // Safari Private Browsing blocks storage - fail silently
  }
}
